/**
 * Search-matching logic for the image-rendered document text layer.
 */

export interface OcrWord {
  text: string;
  left: number;
  top: number;
  width: number;
  height: number;
}

export interface OcrTextLine {
  text: string;
  left: number;
  top: number;
  width: number;
  height: number;
  // Per-word boxes when the OCR provided them
  words?: OcrWord[];
}

/** A normalised (0..1) rectangle on a page. */
export interface MatchRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

/** The portion of one OCR line covered by a match, in that line's own offsets. */
export interface MatchSpan {
  lineIndex: number;
  localStart: number;
  localEnd: number;
}

/** A single phrase occurrence on a page, in document (reading) order. */
export interface PageMatch {
  spans: MatchSpan[];
}

/** A single word-level search match located in the document's OCR text. */
export interface WordMatch {
  pageNumber: number;
  /** Index of this match on its page, in findPageMatches (document) order. */
  indexOnPage: number;
  /** Normalised (0..1) top of the matched word, for scroll targeting. */
  top: number;
}

/**
 * Lowercase + collapse whitespace so matching is case-insensitive and a
 * multi-word query lines up with the single-space-joined page text.
 */
export const normaliseSearchQuery = (query?: string): string => query?.trim().toLowerCase().replace(/\s+/g, ' ') ?? '';

/**
 * The text we search against for a line: the space-joined words when the OCR
 * gave us per-word boxes (so character offsets map straight onto those boxes),
 * otherwise the raw line text. Both the page string and the per-line rect
 * helpers are built from this, so offsets always line up.
 */
export const lineSearchText = (line: OcrTextLine): string =>
  line.words && line.words.length > 0 ? line.words.map((word) => word.text).join(' ') : line.text;

export const areLinesAdjacent = (prev: OcrTextLine, curr: OcrTextLine): boolean => {
  const verticalGap = curr.top - (prev.top + prev.height);
  const horizontallyOverlap = curr.left < prev.left + prev.width && prev.left < curr.left + curr.width;
  return curr.top >= prev.top && verticalGap <= prev.height * 1.5 && horizontallyOverlap;
};

/**
 * Normalised (0..1) rects for characters [start, end) of a line that HAS word
 * boxes. Maps the char range onto the words it covers, interpolating a
 * sub-rectangle within a word box when only part of the word is matched.
 */
export const normalisedWordRects = (words: OcrWord[], start: number, end: number): MatchRect[] => {
  const rects: MatchRect[] = [];
  let offset = 0;

  words.forEach((word, index) => {
    if (index > 0) offset += 1; // the space joined between words
    const wordStart = offset;
    offset += word.text.length;

    const overlapStart = Math.max(wordStart, start);
    const overlapEnd = Math.min(offset, end);
    if (overlapEnd <= overlapStart) return;

    const wordLength = word.text.length || 1;
    const fractionStart = (overlapStart - wordStart) / wordLength;
    const fractionEnd = (overlapEnd - wordStart) / wordLength;

    rects.push({
      left: word.left + fractionStart * word.width,
      top: word.top,
      width: (fractionEnd - fractionStart) * word.width,
      height: word.height,
    });
  });

  return rects;
};

/**
 * Every phrase occurrence on a page, in document order. We match against the
 * whole page (not line by line) so a phrase can wrap across a line break, then
 * map each match back to the line(s) it covers.
 *
 * Adjacent (same-column, consecutive) lines are joined with a space so a match
 * can flow across them.
 */
export const findPageMatches = (lines: OcrTextLine[], rawQuery?: string): PageMatch[] => {
  const query = normaliseSearchQuery(rawQuery);
  if (!query || lines.length === 0) return [];

  const spans: { index: number; start: number; end: number }[] = [];
  let joined = '';
  lines.forEach((line, index) => {
    if (index > 0) {
      joined += areLinesAdjacent(lines[index - 1], line) ? ' ' : '\n';
    }
    const start = joined.length;
    joined += lineSearchText(line);
    spans.push({ index, start, end: joined.length });
  });

  const haystack = joined.toLowerCase();
  const matches: PageMatch[] = [];

  let from = haystack.indexOf(query);
  while (from !== -1) {
    const matchEnd = from + query.length;
    const matchSpans: MatchSpan[] = [];

    for (const span of spans) {
      if (span.start >= matchEnd || span.end <= from) continue;
      matchSpans.push({
        lineIndex: span.index,
        localStart: Math.max(from, span.start) - span.start,
        localEnd: Math.min(matchEnd, span.end) - span.start,
      });
    }

    if (matchSpans.length > 0) matches.push({ spans: matchSpans });
    from = haystack.indexOf(query, matchEnd);
  }

  return matches;
};

/**
 * A normalised (0..1) anchor box for a match — the box we scroll to so the
 * matched word sits in view. Uses the match's first line: word boxes when
 * present (most accurate), otherwise interpolated within the line box by the
 * matched character range.
 */
export const matchAnchorBox = (lines: OcrTextLine[], match: PageMatch): MatchRect | null => {
  const span = match.spans[0];
  if (!span) return null;

  const line = lines[span.lineIndex];
  if (!line) return null;

  if (line.words && line.words.length > 0) {
    const rects = normalisedWordRects(line.words, span.localStart, span.localEnd);
    if (rects.length > 0) {
      const left = Math.min(...rects.map((r) => r.left));
      const top = Math.min(...rects.map((r) => r.top));
      const right = Math.max(...rects.map((r) => r.left + r.width));
      const bottom = Math.max(...rects.map((r) => r.top + r.height));
      return { left, top, width: right - left, height: bottom - top };
    }
  }

  // No word boxes: interpolate within the line box by the matched char range.
  const length = lineSearchText(line).length || 1;
  const fractionStart = span.localStart / length;
  const fractionEnd = span.localEnd / length;
  return {
    left: line.left + fractionStart * line.width,
    top: line.top,
    width: (fractionEnd - fractionStart) * line.width,
    height: line.height,
  };
};
